import { useState, useRef } from 'react';
import { MoreHorizontal, Edit, GraduationCap, RefreshCw, Trash2, Pencil, Copy, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Dance } from '../types';
import Modal from './Modal';

interface DanceCardProps {
  dance: Dance;
  onRename: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
  onRetry?: (id: string) => void;
  onTrain?: (dance: Dance) => void;
  onDeploy?: (id: string) => void;
}

export default function DanceCard({ dance, onRename, onDelete, onRetry, onTrain, onDeploy }: DanceCardProps) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [renameValue, setRenameValue] = useState(dance.name);
  const menuRef = useRef<HTMLDivElement>(null);

  const isProcessing = dance.status === 'generating' || dance.status === 'training';
  const isSuccess = dance.status === 'success';
  const isFailed = dance.status === 'failed';
  const statusLabel = dance.status === 'generating' ? '生成中' : dance.status === 'training' ? '训练中' : dance.status === 'success' ? '成功' : '失败';
  const statusBg = isSuccess ? 'bg-success-50 text-success-dark' : isFailed ? 'bg-danger-50 text-danger-dark' : 'bg-warning-50 text-warning-dark';

  const closeMenu = () => setShowMenu(false);

  return (
    <>
      <div className="bg-white rounded-2xl shadow-card hover-lift group relative overflow-hidden">
        {/* Image */}
        <div className="relative h-64 bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center overflow-hidden rounded-t-2xl">
          <img
            src={dance.imageUrl}
            alt={dance.name}
            className={`w-full h-full object-contain transition-transform duration-700 ease-out ${isProcessing || isFailed ? 'opacity-40 grayscale scale-105' : 'group-hover:scale-108'}`}
          />

          {/* Status badge */}
          <div className={`absolute top-3 left-3 text-[11px] font-semibold px-3 py-1 rounded-full backdrop-blur-sm ${statusBg} shadow-sm`}>
            {statusLabel}{isProcessing ? ` ${Math.round(dance.progress ?? 0)}%` : ''}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 pb-3">
          <div className="flex items-start justify-between mb-1.5">
            <h3 className="text-[14px] font-semibold text-text-primary truncate flex-1">{dance.name}</h3>
          </div>
          <p className="text-[11px] text-text-muted mb-3.5 flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-brand/30 rounded-full" />
            {dance.robotModel}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {dance.type === 'simulation' && isSuccess && dance.stage === 'simulation' && (
              <>
                <button onClick={(e) => { e.stopPropagation(); navigate(`/editor/${dance.id}`); }} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 gradient-brand text-white rounded-xl text-xs font-semibold hover-lift">
                  <Edit size={13} /> 编辑关键帧
                </button>
                <button onClick={(e) => { e.stopPropagation(); onTrain?.(dance); }} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-success-50 text-success-dark rounded-xl text-xs font-semibold hover:bg-success-100 transition-colors border border-success/10">
                  <GraduationCap size={13} /> 训练模型
                </button>
              </>
            )}
            {dance.type === 'simulation' && isSuccess && dance.stage === 'training' && (
              <button onClick={(e) => { e.stopPropagation(); onDeploy?.(dance.id); }} className="w-full py-2.5 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift">
                去App执行
              </button>
            )}
            {isFailed && (
              <button onClick={(e) => { e.stopPropagation(); setShowRetryModal(true); }} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-warning-50 text-warning-dark rounded-xl text-xs font-semibold hover:bg-warning-100 transition-colors border border-warning/10">
                <RefreshCw size={13} /> 重试
              </button>
            )}
            {dance.type === 'model' && isSuccess && (
              <button onClick={(e) => { e.stopPropagation(); onDeploy?.(dance.id); }} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 gradient-brand text-white rounded-xl text-xs font-semibold hover-lift">
                <Smartphone size={13} /> 去App执行
              </button>
            )}
            {isProcessing && (
              <div className="flex-1 py-2.5 bg-slate-50 rounded-xl text-xs text-text-muted text-center border border-border-subtle">
                {dance.type === 'simulation' ? '生成中...' : '训练中...'}
              </div>
            )}

            {/* More menu button */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-all duration-200 shrink-0"
              >
                <MoreHorizontal size={16} className="text-text-muted" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-20" onClick={(e) => { e.stopPropagation(); closeMenu(); }} />
                  <div className="dropdown-enter absolute right-0 bottom-full mb-1 w-40 bg-white rounded-2xl shadow-dropdown py-1.5 z-30">
                    <button onClick={(e) => { e.stopPropagation(); closeMenu(); setRenameValue(dance.name); setShowRenameModal(true); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-text-secondary hover:bg-slate-50 transition-colors">
                      <Pencil size={14} className="text-text-muted" /> 重命名
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); closeMenu(); setShowDeleteModal(true); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-danger hover:bg-danger-50 transition-colors">
                      <Trash2 size={14} /> 删除
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={showRenameModal} onClose={() => setShowRenameModal(false)} title="重命名舞蹈">
        <div onClick={(e) => e.stopPropagation()}>
          <input type="text" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} maxLength={20} placeholder="请输入舞蹈名称" className="w-full px-4 py-3 border border-border-main rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all" />
          <p className="text-xs text-text-muted mt-1.5 text-right">{renameValue.length}/20</p>
          <div className="flex justify-end gap-2.5 mt-5">
            <button onClick={() => setShowRenameModal(false)} className="px-5 py-2.5 text-sm text-text-secondary hover:bg-slate-100 rounded-xl transition-colors font-medium">取消</button>
            <button onClick={() => { onRename(dance.id, renameValue); setShowRenameModal(false); }} className="px-5 py-2.5 text-sm text-white gradient-brand rounded-xl font-medium hover-lift">确认</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="确认删除">
        <div onClick={(e) => e.stopPropagation()}>
          <div className="text-center py-2">
            <div className="w-14 h-14 bg-danger-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Trash2 size={24} className="text-danger" />
            </div>
            <p className="text-sm text-text-primary font-semibold mb-1">确定删除舞蹈「{dance.name}」？</p>
            <p className="text-xs text-text-muted">删除后不可撤销</p>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => setShowDeleteModal(false)} className="flex-1 py-2.5 text-sm text-text-secondary bg-slate-100 rounded-xl font-medium hover:bg-slate-200 transition-colors">取消</button>
            <button onClick={() => { onDelete(dance.id); setShowDeleteModal(false); }} className="flex-1 py-2.5 text-sm text-white bg-danger rounded-xl font-medium hover:bg-danger-dark transition-colors">确认删除</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showRetryModal} onClose={() => setShowRetryModal(false)} title="确认重试">
        <div onClick={(e) => e.stopPropagation()}>
          <div className="text-center py-2">
            <div className="w-14 h-14 bg-warning-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <RefreshCw size={24} className="text-warning" />
            </div>
            <p className="text-sm text-text-primary font-semibold mb-1">确定重新生成「{dance.name}」？</p>
            <p className="text-xs text-text-muted">将重新提交生成任务</p>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => setShowRetryModal(false)} className="flex-1 py-2.5 text-sm text-text-secondary bg-slate-100 rounded-xl font-medium hover:bg-slate-200 transition-colors">取消</button>
            <button onClick={() => { onRetry?.(dance.id); setShowRetryModal(false); }} className="flex-1 py-2.5 text-sm text-white bg-warning rounded-xl font-medium hover:bg-warning-dark transition-colors">确认重试</button>
          </div>
        </div>
      </Modal>
    </>
  );
}
